/**
 * Opt-in abort registry for "abort the previous in-flight request when a new
 * one is made" semantics, keyed per logical endpoint (e.g. an endpoint path).
 *
 * Used for expensive endpoints such as /Consumer/MC, where a superseded request
 * should be cancelled as soon as a newer one is issued.
 */
const controllers = new Map<string, AbortController>();

/**
 * Aborts any in-flight request previously registered under `key`, then registers
 * and returns the signal for a fresh request.
 */
export const nextAbortSignal = (key: string): AbortSignal => {
  controllers.get(key)?.abort();

  const controller = new AbortController();
  controllers.set(key, controller);

  return controller.signal;
};

/**
 * Removes the controller for `key`, but only if it still owns `signal`. This
 * prevents a settled (older) request from clearing a newer request's controller.
 */
export const clearAbortController = (key: string, signal: AbortSignal): void => {
  if (controllers.get(key)?.signal === signal) {
    controllers.delete(key);
  }
};

/**
 * Aborts and forgets any in-flight request registered under `key`. Safe no-op
 * when nothing is in flight. Use to cancel a request on unmount / tab change.
 */
export const abortRequest = (key: string): void => {
  controllers.get(key)?.abort();
  controllers.delete(key);
};
